import * as angular from 'angular';
import * as d3 from 'd3';
import * as moment from 'moment';

import { ComponentDefinition, TicketViewModel, TimeUnit, IApplicationConfiguration, IFrequency } from '../../../../models';
import { ICurrentApplicationService, IDateAlignmentHelperService } from '../../../../services';
import { ChartConfigurationOption } from '../../../../wtFramework';
import { colorScale } from '../colorScale';
import { AllowedPeriods } from '../allowedPeriods';
import { blackTextColor } from '../../../../common';

interface DataFormat {
    timeStamp: any;
    newCount: number;
    completedCount: number;
    closedCount: number;
    completionTime: number;
}

class TicketTrendController implements angular.IComponentController {
    isLoading: boolean;
    loadingMessage: string;
    data: DataFormat[];
    series: any[];
    options: ChartConfigurationOption;
    tickets: TicketViewModel[];
    beginDate: string;
    endDate: string;
    selectedPeriod: AllowedPeriods;
    // Object to attach zoom functionalities at upper level
    zoomControl: any;

    private _mappingDic = {
        'd': TimeUnit.Day,
        'w': TimeUnit.Week,
        'M': TimeUnit.Month
    };
    private _unsubscribe: () => void;

    static $inject = ['$rootScope', '$translate', 'dateAlignmentHelperService'];

    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$translate: angular.translate.ITranslateService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _currentApplicationService: ICurrentApplicationService
    ) {
    }

    $onInit() {
        this.data = this.data || [];
        this.series = [{
            color: colorScale('New'),
            identifier: 'New',
            selector: d => 'New',
            value: d => d.newCount,
            title: 'New',
            name: 'New',
            isVisible: true,
            isLineVisible: false,
            isBarsVisible: true,
            isStacked: false,
            pane: 0
        },
        {
            color: colorScale('Completed'),
            identifier: 'Completed',
            selector: d => 'Completed',
            value: d => d.completedCount,
            title: 'Completed',
            name: 'Completed',
            isVisible: true,
            isLineVisible: false,
            isBarsVisible: true,
            isStacked: false,
            pane: 0
        },
        {
            color: colorScale('Closed'),
            identifier: 'Closed',
            selector: d => 'Closed',
            value: d => d.closedCount,
            title: 'Closed',
            name: 'Closed',
            isVisible: true,
            isLineVisible: false,
            isBarsVisible: true,
            isStacked: false,
            pane: 0
        },
        {
            color: blackTextColor,
            identifier: 'completionTime',
            selector: d => 'completionTime',
            value: d => d.completionTime,
            title: 'Completion time [d]',
            name: this._$translate.instant('Completion time [d]'),
            isVisible: true,
            isLineVisible: true,
            isBarsVisible: false,
            isStacked: false,
            hasOwnAxis: true,
            yMin: 0,
            pane: 0,
            crosshairFormat: (d: DataFormat) => d3.format('.2f')(d.completionTime)
        }];
        const labels = this.series.map(s => s.name);

        const options = new ChartConfigurationOption();
        options.isTitleVisible = false;
        options.isLegendVisible = true;
        options.isHorizontalGridlinesVisible = false;
        options.panes = [{
            height: 1,
            areas: []
        }];
        this.options = options;
        this._unsubscribe = this._$rootScope.$on('$translateChangeSuccess', () => {
            this.series = this.series.map((s, i) => {
                s.title = this._$translate.instant(labels[i]);
                s.name = s.title;
                return s;
            });
        });
        this.zoomControl = {};
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (this.tickets && this.beginDate && this.endDate && this.selectedPeriod) {
            const config = this._currentApplicationService.getApplicationConfiguration();
            const dates = this._dateAlignmentHelperService.getRangeSync(new Date(this.beginDate), new Date(this.endDate),
                { interval: 1, intervalTimeUnit: this._mappingDic[this.selectedPeriod] }, config);
            const creationDates = this.tickets.map(t => this.computePrevious(moment(t.model.creationDate), config).toISOString());
            const completedDates = this.tickets.map(t => t.model.completedDate)
                .filter(t => !!t)
                .map(t => this.computePrevious(moment(t), config).toISOString());
            const closedDates = this.tickets.map(t => t.model.closedDate)
                .filter(t => !!t)
                .map(t => this.computePrevious(moment(t), config).toISOString());
            const completionTimes = this.tickets
                .map(t => {
                    return {
                        creationDate: t.model.creationDate,
                        closeDate: t.model.closedDate ?? t.model.completedDate
                    };
                })
                .filter(d => !!d.closeDate)
                .map(d => {
                    const m = moment(d.closeDate);
                    return {
                        closedDate: this.computePrevious(m, config).toISOString(),
                        completionTime: m.diff(moment(d.creationDate))
                    };
                });
            const data = dates.map(d => {
                const isoString = d.toISOString();
                const closed = completionTimes.filter(d => d.closedDate === isoString);
                const meanTime = closed.length ? d3.mean(closed, c => c.completionTime) : 0;
                return {
                    timeStamp: d,
                    newCount: creationDates.filter(t => t === isoString).length,
                    completedCount: completedDates.filter(t => t === isoString).length,
                    closedCount: closedDates.filter(t => t === isoString).length,
                    completionTime: moment.duration(meanTime).asDays()
                };
            });
            this.data = data;
        }
    }

    $onDestroy() {
        this._unsubscribe();
    }


    private computePrevious(input: moment.Moment, config: IApplicationConfiguration): moment.Moment {
        return this._dateAlignmentHelperService.previous(
            input,
            { interval: 1, intervalTimeUnit: this._mappingDic[this.selectedPeriod] } as IFrequency,
            config.beginDayTime,
            config.weekBeginDay,
            config.timeZone
        );
    }
}

export const wtTicketTrend: ComponentDefinition = {
    componentId: 'wtTicketTrend',
    controller: TicketTrendController,
    template: require('./ticketTrend.component.html'),
    controllerAs: 'vm',
    bindings: {
        isLoading: '<',
        tickets: '<',
        beginDate: '<',
        endDate: '<',
        selectedPeriod: '<',
        zoomControl: '='
    }
};